import { create } from 'zustand';

import { FinalImpressionsForm } from '@/shared/model';
import { toNextRecommendedScanOption } from '@/shared/util';

interface SetAddendumArgs {
  addendum: string;
  addendumUuid: null | string;
}

interface FinalImpressionsFormActions {
  initializeFinalImpressionsForm: (report: FinalImpressionsForm) => void;
  setAddendum: (args: SetAddendumArgs) => void;
  updateFinalImpressionsForm: (updates: Partial<Omit<FinalImpressionsForm, 'addendum'>>) => void;
}

type FinalImpressionsFormStore = FinalImpressionsForm &
  FinalImpressionsFormActions & {
    addendumUuid: null | string;
  };

const initialState: FinalImpressionsForm & { addendumUuid: null | string } = {
  addendum: '',
  addendumUuid: null,
  changeFromPrevious: null,
  dateOfPrevious: null,
  finalImpressions: '',
  nextRecommendedScan: 12,
  urgencyCategory: null,
};

export const useFinalImpressionsFormStore = create<FinalImpressionsFormStore>((set) => ({
  ...initialState,

  initializeFinalImpressionsForm: (report: FinalImpressionsForm) => {
    set({
      changeFromPrevious: report.changeFromPrevious,
      dateOfPrevious: report.dateOfPrevious,
      finalImpressions: String(report.finalImpressions || ''),
      nextRecommendedScan: toNextRecommendedScanOption(report.nextRecommendedScan),
      urgencyCategory: report.urgencyCategory ?? null,
    });
  },

  setAddendum: ({ addendum, addendumUuid }: SetAddendumArgs) => {
    set({ addendum, addendumUuid });
  },

  updateFinalImpressionsForm: (updates: Partial<Omit<FinalImpressionsForm, 'addendum'>>) => {
    set((state) => {
      const newState: Partial<FinalImpressionsForm> = {};

      if (updates.finalImpressions !== undefined) {
        newState.finalImpressions = String(updates.finalImpressions || '');
      }

      if (updates.nextRecommendedScan !== undefined) {
        newState.nextRecommendedScan = toNextRecommendedScanOption(updates.nextRecommendedScan);
      }

      if (updates.dateOfPrevious !== undefined) {
        newState.dateOfPrevious = updates.dateOfPrevious;
      }

      if (updates.changeFromPrevious !== undefined) {
        newState.changeFromPrevious = updates.changeFromPrevious;
      }

      if (updates.urgencyCategory !== undefined) {
        newState.urgencyCategory = updates.urgencyCategory;
      }

      return {
        ...state,
        ...newState,
      };
    });
  },
}));
